import { Clock } from '@app/contexts/Clock';
import { Schedule } from '@app/types';
import { addDays, differenceInMilliseconds, isAfter, isBefore, subDays } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';

type Options = {
  isBreakTime: boolean;
  isAPublicMenu: boolean;
};

export default function useTimeManager(
  schedules: Schedule[] | null | undefined,
  clock: Clock,
  options: Options
): TimeManager {
  const [timeManager, setTimeManager] = useState(createTimeManager(clock.now(), schedules ?? [], options));

  useEffect(() => {
    setTimeManager(createTimeManager(clock.now(), schedules ?? [], options));
  }, [clock, schedules]);

  const revalidate = useCallback(() => {
    setTimeManager(createTimeManager(clock.now(), schedules ?? [], options));
  }, [clock, schedules]);

  const isReady = useMemo(() => {
    return !!clock.now();
  }, [clock]);

  return { ...timeManager, revalidate, isReady };
}

export type TimeManager = {
  revalidate: () => void;
  openAt: Date | null;
  closeAt: Date | null;
  isOpen: () => boolean;
  notOpenToday: () => boolean;
  milliUntilClose: () => number;
  milliUntilOpen: () => number;
  hasPassedBusinessHours: () => boolean;
  hasPassedClosingTime: () => boolean;
  currentSchedule: Schedule | null;
  isReady: boolean;
};

function createTimeManager(now: Date | null, schedules: Schedule[], options: Options) {
  const { isBreakTime, isAPublicMenu } = options;
  const currentSchedule = schedules?.[now?.getDay() ?? 0];

  if (timeManagerIsNotReady()) return notReadyProperties();

  const nowDate = now!;
  const { openAt, closeAt } = getNextCloseAndOpenDate();
  const currentDate = getCurrentDate();

  return {
    openAt: openAt,
    closeAt: closeAt,
    isOpen: isOpen,
    milliUntilClose: calculateMilliUntilClose,
    milliUntilOpen: calculateMilliUntilOpen,
    hasPassedBusinessHours: hasPassedBusinessHours,
    hasPassedClosingTime: hasPassedClosingTime,
    notOpenToday,
    currentSchedule: currentSchedule as Schedule,
  };

  function getCurrentDate() {
    if (!openAt || !closeAt) return now!;

    if (closeInNextDay(openAt, closeAt) && isBeforeCloseDate()) return addDays(now!, 1);
    return now!;

    function isBeforeCloseDate() {
      return now!.getHours() < closeAt!.getHours();
    }
  }

  function isOpen() {
    if (isBreakTime && isAPublicMenu) return false;
    if (!currentSchedule) return false;
    if (currentSchedule.VinteQuatroHoras) return true;
    if (!openAt || !closeAt) return false;
    if (currentSchedule.Fechado) return false;
    return isAfter(currentDate, openAt) && isBefore(currentDate, closeAt);
  }

  function notOpenToday() {
    if (!currentSchedule) return false;
    return currentSchedule.Fechado;
  }

  function calculateMilliUntilClose() {
    return isOpen() && closeAt ? differenceInMilliseconds(closeAt, currentDate) : 0;
  }

  function calculateMilliUntilOpen() {
    return !isOpen() && openAt ? differenceInMilliseconds(openAt, currentDate) : 0;
  }

  function hasPassedBusinessHours() {
    return !isOpen() && calculateMilliUntilOpen() < 0;
  }

  function hasPassedClosingTime() {
    return isOpen() && calculateMilliUntilClose() < 0;
  }

  function timeManagerIsNotReady() {
    return !currentSchedule || !now;
  }

  function notReadyProperties() {
    return {
      openAt: null,
      closeAt: null,
      hasPassedBusinessHours: () => false,
      hasPassedClosingTime: () => false,
      isOpen: () => false,
      milliUntilClose: () => -1,
      milliUntilOpen: () => -1,
      notOpenToday: () => false,
      currentSchedule: null,
    };
  }

  function getNextCloseAndOpenDate(): NextCloseAndOpenDate {
    if (dontNeedCalculate()) return { openAt: null, closeAt: null };
    if (currentSchedule.HorarioAlmoco) return calculateWithLunchTime();
    return calculateWithoutLunchTime();

    function calculateWithLunchTime() {
      const openDate = parseTimeStringToDate(currentSchedule.Abre);
      const closeToLunch = parseTimeStringToDate(currentSchedule.FechaAlmoco as string);
      const backFromLunch = parseTimeStringToDate(currentSchedule.VoltaAlmoco as string);
      const closeDate = parseTimeStringToDate(currentSchedule.Fecha);

      const isLunchTime = isAfter(nowDate, closeToLunch) && isBefore(nowDate, backFromLunch);
      const isOpenAndIsBeforeLunchTime = isAfter(nowDate, openDate) && isBefore(nowDate, closeToLunch);

      const finalOpenDate = isLunchTime ? backFromLunch : openDate;
      const finalCloseDate = isOpenAndIsBeforeLunchTime ? closeToLunch : closeDate;

      return { openAt: finalOpenDate, closeAt: finalCloseDate };
    }

    function calculateWithoutLunchTime() {
      const openDate = parseTimeStringToDate(currentSchedule.Abre);
      const closeDate = parseTimeStringToDate(currentSchedule.Fecha);

      return {
        openAt: openDate,
        closeAt: closeInNextDay(openDate, closeDate) ? addDays(closeDate, 1) : closeDate,
      };
    }

    function dontNeedCalculate() {
      return currentSchedule.VinteQuatroHoras || (!currentSchedule.Abre && !currentSchedule.Fecha) || isBreakTime;
    }

    function parseTimeStringToDate(hoursText: string | null | undefined) {
      const [hours, minutes, seconds] = hoursText?.split(':').map((h) => Number(h)) ?? [0, 0, 0];
      return new Date(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate(), hours, minutes, seconds);
    }
  }
}

type NextCloseAndOpenDate = {
  openAt: Date | null;
  closeAt: Date | null;
};

function closeInNextDay(openDate: Date, closeDate: Date) {
  return closeDate.getHours() < openDate.getHours();
}
